/**
 * Onboarding · Step 10 — Make it yours: take the next steps.
 *
 * The finale. By this point the user has walked the entire sample flow
 * (drop → deck → publish). This step doesn't introduce new mechanics —
 * it consolidates the two directions that most directly extend what
 * they just experienced:
 *
 *   1. Connect your stack    — Drive/Gmail/Notion/Slack/HubSpot land as
 *      nodes on the canvas the same way the sample brief did. The card
 *      opens the Settings dialog on the Connectors section.
 *   2. Teach it your skills  — turn any prompt/format/workflow into a
 *      reusable #skill the agent applies automatically. The card opens
 *      the Settings dialog on the Skills section.
 *
 * Both cards are clickable: activating one finishes onboarding (so the
 * welcome dialog never reappears) and opens the Settings dialog to the
 * relevant section — the same `useOpenSettings` path the canvas
 * toolbar's Skills button uses. The "Let's go" CTA finishes onboarding
 * without opening anything. No spotlight target — the splash card
 * centers and reads.
 */

import { ROUTES } from "@/constants/routes";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import { useSidebarContext } from "@/context/SidebarContext";
import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useCanvasOnboardingUpdate } from "../../../../context/CanvasOnboardingContext";
import { Flourish, StepHeader } from "../shared/StepHeader";
import type { StepProps } from "../shared/types";

const FINISH_BEAT_MS = 760;

/** Display/serif face — same stack the heading flourish uses. */
const SERIF_FONT = "'Instrument Serif', 'Iowan Old Style', 'Georgia', serif";

/** Shared static bucket that hosts the monochrome integration logos. */
const LOGO_BASE = `${import.meta.env.VITE_STATIC_BASE_URL || ""}/images/integrations/logos`;

/** Brand logos for the "Connect your stack" row, in display order. */
const STACK_LOGOS: ReadonlyArray<{ name: string; file: string }> = [
  { name: "Google Drive", file: "google-drive.svg" },
  { name: "Gmail", file: "gmail.svg" },
  { name: "Notion", file: "notion.svg" },
  { name: "Slack", file: "slack.svg" },
  { name: "HubSpot", file: "hubspot.svg" },
];

/**
 * Shared icon-container box used by the "Teach it your skills" block
 * (and matched in height by the logo row). 36×36 rounded square, paper
 * fill, rust foreground.
 */
const ICON_BOX: CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 10,
  background: "var(--co-paper)",
  border: "1px solid var(--co-border)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--co-rust)",
  flexShrink: 0,
  alignSelf: "flex-start",
};

interface Pillar {
  /** Rendered icon-area content — includes its own container. */
  media: ReactNode;
  title: string;
  body: ReactNode;
  /**
   * Settings path passed to `useOpenSettings` when the card is activated.
   * Must match an AdminPanel settings menu item path.
   */
  settingsPath?: string;
  /** When set, opens the Skills left rail instead of Settings. */
  openSkillsRail?: boolean;
}

const PILLARS: readonly Pillar[] = [
  {
    media: <LogoRow />,
    title: "Connect your stack",
    body: "Connect Drive, Gmail, Notion, Slack, HubSpot, and more. Anything you drop or @mention lands on the canvas as a live node your agent can read, reason over, and act on.",
    settingsPath: ROUTES.SETTINGS.CONNECTORS,
  },
  {
    media: <HashGlyph />,
    title: "Teach it your skills",
    body: "Turn any prompt, format, or workflow into a reusable #skill. Your agent applies them automatically.",
    openSkillsRail: true,
  },
];

export const MakeItYoursStep = memo(function MakeItYoursStep({
  onFinish,
}: StepProps) {
  const update = useCanvasOnboardingUpdate();
  const openSettings = useOpenSettings();
  const { selectRail } = useSidebarContext();
  const [finishing, setFinishing] = useState(false);

  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  useEffect(() => {
    update({
      lockedViewport: false,
      highlightTargetId: null,
      pinnedComposerPrompt: null,
    });
    return () => {
      update({ highlightTargetId: null });
    };
  }, [update]);

  const handleFinish = useCallback(() => {
    if (finishing) return;
    setFinishing(true);
    window.setTimeout(() => onFinishRef.current(), FINISH_BEAT_MS);
  }, [finishing]);

  // Card activation: finish onboarding (marks it complete + tears down
  // the onboarding chat session), then open the Settings dialog on the
  // relevant section — the same `useOpenSettings` path the canvas
  // toolbar's Skills button uses.
  const handleCardActivate = useCallback(
    (pillar: Pillar) => {
      if (finishing) return;
      setFinishing(true);
      onFinishRef.current();
      if (pillar.openSkillsRail) {
        selectRail("skills");
        return;
      }
      if (pillar.settingsPath) {
        openSettings(pillar.settingsPath);
      }
    },
    [finishing, openSettings, selectRail],
  );

  // Allow Enter / Space to fire the finish CTA — matches the keyboard
  // affordance on the opening DragMark step so the bookends feel
  // symmetric.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Enter") return;
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if ((e.target as HTMLElement | null)?.isContentEditable) return;
      e.preventDefault();
      handleFinish();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleFinish]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--co-onb-stack-md)",
        width: "100%",
      }}
    >
      <StepHeader
        heading={
          <>
            Make it <Flourish>yours</Flourish>. Take the{" "}
            <Flourish>next steps</Flourish>.
          </>
        }
        lede={
          <>
            You just took a sample brief from drop to deck. Here&apos;s how to
            make Cohesive work on your work, your way.
          </>
        }
      />

      {/* Pillar grid — two clickable tiles, equal columns */}
      <div
        className="_co_onb_settle_3"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 12,
          marginTop: "var(--co-onb-stack-xs)",
        }}
      >
        {PILLARS.map((p) => (
          <PillarCard
            key={p.title}
            {...p}
            onActivate={() => handleCardActivate(p)}
          />
        ))}
      </div>

      {/* Finish CTA — bottom-right, mirrors step 2's "Begin tour" rhythm
          so the opening and closing beats feel like a pair. */}
      <div
        className="_co_onb_settle_4"
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "flex-end",
          alignSelf: "flex-end",
          marginTop: "var(--co-onb-stack-sm)",
        }}
      >
        <button
          type="button"
          className={`nodrag _co_onb_finish${finishing ? " _co_onb_finish_pop" : ""}`}
          onClick={handleFinish}
          onPointerDown={(e) => e.stopPropagation()}
          disabled={finishing}
          aria-label="Finish onboarding"
          style={{
            position: "relative",
            minHeight: 40,
            paddingLeft: 20,
            paddingRight: 20,
            fontSize: "var(--co-onb-fs-cta)",
            gap: 8,
          }}
        >
          {finishing ? "On you go" : "Let's go"}
          {!finishing && <Arrow />}
        </button>
        {finishing && (
          <>
            <span aria-hidden className="_co_onb_finish_ring" />
            <span
              aria-hidden
              className="_co_onb_finish_ring _co_onb_finish_ring_late"
            />
          </>
        )}
      </div>
    </div>
  );
});

// ─── PillarCard ───────────────────────────────────────────────────────────────

const PillarCard = memo(function PillarCard({
  media,
  title,
  body,
  onActivate,
}: Pick<Pillar, "media" | "title" | "body"> & { onActivate: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      className="nodrag"
      onClick={onActivate}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      style={{
        appearance: "none",
        margin: 0,
        font: "inherit",
        cursor: "pointer",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        padding: "16px 14px",
        borderRadius: 14,
        background: "var(--co-snap-fill)",
        border: `1px solid ${hover ? "var(--co-rust)" : "var(--co-border)"}`,
        boxShadow: hover ? "var(--co-cta-shadow)" : "none",
        transform: hover ? "translateY(-2px)" : "translateY(0)",
        transition:
          "transform 160ms cubic-bezier(0.2,0,0,1), border-color 160ms ease, box-shadow 160ms ease",
        textAlign: "left",
        minWidth: 0,
      }}
    >
      {media}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: "var(--co-fg)",
          fontFamily: "inherit",
        }}
      >
        {title}
        <span
          aria-hidden
          style={{
            display: "inline-flex",
            color: "var(--co-rust)",
            opacity: hover ? 1 : 0,
            transform: hover ? "translateX(0)" : "translateX(-3px)",
            transition: "opacity 160ms ease, transform 160ms ease",
          }}
        >
          <Arrow />
        </span>
      </div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 400,
          lineHeight: 1.5,
          letterSpacing: "-0.005em",
          color: "var(--co-fg-muted)",
          fontFamily: "inherit",
        }}
      >
        {body}
      </div>
    </button>
  );
});

// ─── LogoRow (Block 1 icon treatment) ─────────────────────────────────────────

/**
 * Horizontal row of monochrome brand logos for the "Connect your stack"
 * block. Each logo is rendered as a CSS mask over a rust fill so the
 * row reads as one unified element rather than a colorful sticker
 * sheet, regardless of the source SVG's own colors. Sized to match the
 * height of the `ICON_BOX` used by blocks 2 & 3.
 */
function LogoRow() {
  return (
    <span
      aria-hidden
      style={{
        height: 36,
        borderRadius: 10,
        background: "var(--co-paper)",
        border: "1px solid var(--co-border)",
        display: "inline-flex",
        alignItems: "center",
        gap: 9,
        padding: "0 11px",
        alignSelf: "flex-start",
        maxWidth: "100%",
      }}
    >
      {STACK_LOGOS.map((logo) => (
        <span
          key={logo.name}
          title={logo.name}
          style={{
            width: 17,
            height: 17,
            flexShrink: 0,
            display: "inline-block",
            backgroundColor: "var(--co-rust)",
            WebkitMaskImage: `url(${LOGO_BASE}/${logo.file})`,
            maskImage: `url(${LOGO_BASE}/${logo.file})`,
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            maskPosition: "center",
            WebkitMaskSize: "contain",
            maskSize: "contain",
          }}
        />
      ))}
      <span
        style={{
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: "-0.005em",
          color: "var(--co-rust)",
          fontFamily: "inherit",
          whiteSpace: "nowrap",
        }}
      >
        + more
      </span>
    </span>
  );
}

// ─── HashGlyph (Block 2 icon treatment) ───────────────────────────────────────

/**
 * The `#` mark for the "Teach it your skills" block — rendered in the
 * display serif inside the shared `ICON_BOX` so it reads as a
 * typographic mark rather than a UI symbol.
 */
function HashGlyph() {
  return (
    <span aria-hidden style={ICON_BOX}>
      <span
        style={{
          fontFamily: SERIF_FONT,
          fontSize: 24,
          lineHeight: 1,
          fontWeight: 400,
          color: "var(--co-rust)",
          transform: "translateY(0.5px)",
        }}
      >
        #
      </span>
    </span>
  );
}

// ─── Arrow ────────────────────────────────────────────────────────────────────

const Arrow = memo(function Arrow() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden
      style={{ transform: "translateY(0.5px)" }}
    >
      <path
        d="M3 7h8m0 0L7.5 3.5M11 7l-3.5 3.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
});
